import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfWatchlistV1Fincen314FileQueryServiceFincen314FileGet } from '@interxfi/api-client/generated/hooks/ixf-watchlist-v1-fincen314-file-query-service-fincen314-file-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxfWatchlistV1Fincen314FileState } from '@interxfi/types';
import { FincenEntriesTable } from '../../../../../../../../../../features/watchlist/fincen/entry/fincen-entry-table';
import { FincenFileEventDrawer, FincenFileEventSummary } from '../../../../../../../../../../features/watchlist/fincen/file/fincen-file-event';
import { getFincenFileStateListItems } from '../../../../../../../../../../features/watchlist/fincen/file/fincen-file-list-items';
import { getFincenFileDetailPath } from '../../../../../../../../../../features/watchlist/fincen/file/fincen-file-routing';
import { WatchlistFincenFileStatusPill } from '../../../../../../../../../../features/watchlist/fincen/file/fincen-file-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/fincen/file/_file/$fincen314FileId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `fincen-file-${params.fincen314FileId}`,
        label: 'File',
        to: getFincenFileDetailPath(params.fincen314FileId),
        suffix: (
          <CopyButton
            content={params.fincen314FileId}
            label="Copy FinCEN File ID"
            tooltip={{ copy: params.fincen314FileId, copied: 'Copied FinCEN File ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('FinCEN File'),
  component: FincenFileDetail,
});

function getMenuItems(fincen314FileId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: FincenFileEventDrawer,
        props: { request: { fincen314FileId } },
      },
    },
    actions: undefined,
  };
}

function getListItems(data: IxfWatchlistV1Fincen314FileState | undefined) {
  const items = getFincenFileStateListItems(data);

  return {
    left: [items.data.documentUrl, items.data.uploadedBy, items.data.totalEntities] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.fincen314FileId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load FinCEN file', 'view this FinCEN file');

function FincenFileDetail() {
  const { fincen314FileId } = Route.useParams();
  const request = { fincen314FileId } as const;
  const { data, error, isPending } = useIxfWatchlistV1Fincen314FileQueryServiceFincen314FileGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.fincen314File), [data?.fincen314File]);
  const menuItems = useMemo(() => getMenuItems(fincen314FileId), [fincen314FileId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <WatchlistFincenFileStatusPill isPending={showPending} size="large" status={data?.fincen314File?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FincenFileEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Entries</Typography.Level3>
        </VerticalStack>

        <FincenEntriesTable fincen314FileId={fincen314FileId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
